import React from 'react';
import {
  Controller, SubmitHandler, useFieldArray, useFormContext, useWatch,
} from 'react-hook-form';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import moment from 'moment';
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import {
  CustomRichTextField,
  ErrorMessage,
  Label,
  PrimaryLoadingButton,
  PrimaryOutlinedButton,
  PrimaryDatePicker,
  PrimaryTooltip,
  PrimaryTimePicker,
  PrimaryTextField,
} from 'components';
import { CharacterCountDisplay } from 'components/molecules/primary-text-field';
import { SessionAudienceTypes } from 'config';
import { IEventLocationsForm, CourseOnlySessionStatus } from '../../../../interface';
import {
  eventLocationsFieldConfigs,
  MULTI_DAY_EVENT_END_TIME,
  MULTI_DAY_EVENT_START_TIME,
} from '../../validation/event-locations-validation';

interface EventLocationsFormProps {
  onSubmit: SubmitHandler<IEventLocationsForm>;
  loading: boolean;
  audienceType: string;
  courseOnlyStatus: string;
  editMode: boolean;
}

const EventLocationsForm = ({
  onSubmit,
  loading,
  audienceType,
  courseOnlyStatus,
  editMode,
}: EventLocationsFormProps) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
  } = useFormContext<IEventLocationsForm>();
  const watchedLocations = useWatch({
    control,
    name: 'locations',
  });

  const {
    fields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'locations',
  });

  const handleAddLocation = () => {
    if (fields.length >= 7) {
      return;
    }
    append({
      startDate: '',
      endDate: '',
      from: '',
      to: '',
      address: '',
      description: '',
      isNewLocation: true,
    });
  };

  const handleRemoveLocation = (index: number) => {
    if (fields.length <= 1) {
      return;
    }
    remove(index);
  };

  const getMinTimeForLocation = (index: number) => {
    const dateValue = getValues(`locations.${index}.startDate`);
    if (!dateValue) return undefined;

    const selectedDate = moment(dateValue, 'YYYY-MM-DD', true);
    const today = moment().startOf('day');

    if (selectedDate.isAfter(today)) {
      return undefined;
    }

    if (selectedDate.isSame(today)) {
      const now = moment();
      const roundedNow = now.clone().add(1, 'minute').startOf('minute');
      return roundedNow;
    }

    return undefined;
  };

  const isMultiDayLocation = (index: number) => {
    const startDate = watchedLocations?.[index]?.startDate;
    const endDate = watchedLocations?.[index]?.endDate;

    if (!startDate || !endDate) {
      return false;
    }

    return !moment(startDate, 'YYYY-MM-DD', true).isSame(moment(endDate, 'YYYY-MM-DD', true), 'day');
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={2} className="form-item">
        {fields.map((field, index) => (
          <Grid item xs={12} key={field.id}>
            <Typography fontWeight={600}>{`Location ${index + 1}`}</Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={3}>
                <Controller
                  control={control}
                  name={`locations.${index}.startDate`}
                  render={({ field: controllerField }) => (
                    <PrimaryDatePicker
                      {...controllerField}
                      label="Start Date"
                      placeholder="Select start date"
                      value={controllerField.value ? moment(controllerField.value, 'YYYY-MM-DD') : null}
                      onChange={(value: any) => {
                        const formattedStartDate = value ? moment(value).format('YYYY-MM-DD') : '';
                        controllerField.onChange(formattedStartDate);

                        const endDate = getValues(`locations.${index}.endDate`);
                        const endDateShouldMatchStart = (
                          formattedStartDate
                          && endDate
                          && moment(endDate, 'YYYY-MM-DD', true)
                            .isBefore(moment(formattedStartDate, 'YYYY-MM-DD', true), 'day')
                        );
                        if (endDateShouldMatchStart) {
                          setValue(`locations.${index}.endDate`, formattedStartDate);
                        }
                        const normalizedEndDate = endDateShouldMatchStart
                          ? formattedStartDate
                          : endDate;

                        if (
                          formattedStartDate
                          && normalizedEndDate
                          && !moment(formattedStartDate, 'YYYY-MM-DD', true).isSame(moment(normalizedEndDate, 'YYYY-MM-DD', true), 'day')
                        ) {
                          setValue(`locations.${index}.from`, MULTI_DAY_EVENT_START_TIME);
                          setValue(`locations.${index}.to`, MULTI_DAY_EVENT_END_TIME);
                        }
                      }}
                    />
                  )}
                />
                <ErrorMessage>
                  {(errors as any)?.locations?.[index]?.startDate?.message}
                </ErrorMessage>
              </Grid>
              <Grid item xs={3}>
                <Controller
                  control={control}
                  name={`locations.${index}.from`}
                  render={({ field: controllerField }) => (
                    <PrimaryTimePicker
                      {...controllerField}
                      label="From"
                      value={controllerField.value ? moment(controllerField.value, 'HH:mm:ss') : null}
                      minTime={getMinTimeForLocation(index)}
                      disabled={isMultiDayLocation(index)}
                      onChange={(value: any) => {
                        controllerField.onChange(value ? moment(value).format('HH:mm:ss') : '');
                      }}
                    />
                  )}
                />
                <ErrorMessage>{(errors as any)?.locations?.[index]?.from?.message}</ErrorMessage>
              </Grid>
              <Grid item xs={3}>
                <Controller
                  control={control}
                  name={`locations.${index}.endDate`}
                  render={({ field: controllerField }) => (
                    <PrimaryDatePicker
                      {...controllerField}
                      label="End Date"
                      placeholder="Select end date"
                      minDate={watchedLocations?.[index]?.startDate
                        ? moment(watchedLocations[index].startDate, 'YYYY-MM-DD').toDate()
                        : false}
                      value={controllerField.value ? moment(controllerField.value, 'YYYY-MM-DD') : null}
                      onChange={(value: any) => {
                        const formattedEndDate = value ? moment(value).format('YYYY-MM-DD') : '';
                        const startDate = getValues(`locations.${index}.startDate`);
                        if (
                          startDate
                          && formattedEndDate
                          && moment(formattedEndDate, 'YYYY-MM-DD', true)
                            .isBefore(moment(startDate, 'YYYY-MM-DD', true), 'day')
                        ) {
                          controllerField.onChange(startDate);
                          return;
                        }

                        controllerField.onChange(formattedEndDate);

                        if (
                          startDate
                          && formattedEndDate
                          && !moment(startDate, 'YYYY-MM-DD', true).isSame(moment(formattedEndDate, 'YYYY-MM-DD', true), 'day')
                        ) {
                          setValue(`locations.${index}.from`, MULTI_DAY_EVENT_START_TIME);
                          setValue(`locations.${index}.to`, MULTI_DAY_EVENT_END_TIME);
                        }
                      }}
                    />
                  )}
                />
                <ErrorMessage>{(errors as any)?.locations?.[index]?.endDate?.message}</ErrorMessage>
              </Grid>
              <Grid item xs={3}>
                <Controller
                  control={control}
                  name={`locations.${index}.to`}
                  render={({ field: controllerField }) => (
                    <PrimaryTimePicker
                      {...controllerField}
                      label="To"
                      value={controllerField.value ? moment(controllerField.value, 'HH:mm:ss') : null}
                      minTime={getMinTimeForLocation(index)}
                      disabled={isMultiDayLocation(index)}
                      onChange={(value: any) => {
                        controllerField.onChange(value ? moment(value).format('HH:mm:ss') : '');
                      }}
                    />
                  )}
                />
                <ErrorMessage>{(errors as any)?.locations?.[index]?.to?.message}</ErrorMessage>
              </Grid>
              {isMultiDayLocation(index) && (
                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Multi-day timing info
                    </Typography>
                    <PrimaryTooltip
                      title={(
                        <Typography variant="body1" sx={{ fontSize: '14px', color: '#fff' }}>
                          Start Time and End Time are fixed to 00:00 and 23:59 for multi-day events.
                          Please provide individual daily timings in the Description field.
                        </Typography>
                      )}
                    >
                      <IconButton sx={{ ml: 0.5, p: 0.5, color: 'primary.main' }}>
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </PrimaryTooltip>
                  </Box>
                </Grid>
              )}
              <Grid item xs={8}>
                <Controller
                  control={control}
                  name={`locations.${index}.address`}
                  render={({ field: controllerField }) => (
                    <>
                      <PrimaryTextField
                        {...controllerField}
                        label="Address"
                        placeholder="Enter address"
                        disabled={editMode && !watchedLocations?.[index]?.isNewLocation}
                      />
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.5,
                      }}
                      >
                        <ErrorMessage>
                          {(errors as any)?.locations?.[index]?.address?.message}
                        </ErrorMessage>
                        <CharacterCountDisplay
                          value={controllerField.value || ''}
                          config={eventLocationsFieldConfigs.address}
                        />
                      </Box>
                    </>
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  control={control}
                  name={`locations.${index}.description`}
                  render={({ field: controllerField }) => (
                    <>
                      <Label>
                        Location description
                      </Label>
                      <CustomRichTextField
                        {...controllerField}
                        placeholder="Type here..."
                        defaultValue={field.description || ''}
                        options={{
                          options: ['inline', 'blockType', 'list', 'textAlign', 'emoji', 'remove', 'history'],
                        }}
                      />
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mt: 0.5,
                      }}
                      >
                        <ErrorMessage>
                          {(errors as any)?.locations?.[index]?.description?.message}
                        </ErrorMessage>
                        <CharacterCountDisplay
                          value={controllerField.value || ''}
                          config={eventLocationsFieldConfigs.description}
                          mode="richtext"
                        />
                      </Box>
                    </>
                  )}
                />
              </Grid>
            </Grid>
            {fields.length > 1 && (
              <>
                <PrimaryOutlinedButton
                  type="button"
                  onClick={() => handleRemoveLocation(index)}
                >
                  Remove
                </PrimaryOutlinedButton>
                <Grid item xs={8}>
                  <Divider />
                </Grid>
              </>
            )}
          </Grid>
        ))}
      </Grid>

      <Grid container justifyContent="flex-start" className="form-item" spacing={2}>
        <Grid item>
          <PrimaryOutlinedButton type="button" onClick={handleAddLocation} disabled={fields.length >= 7}>
            <AddIcon sx={{ marginRight: '0.5rem' }} />
            Add location
          </PrimaryOutlinedButton>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs>
          <PrimaryLoadingButton loading={loading} type="submit">
            {audienceType === SessionAudienceTypes.courseOnly
              ? courseOnlyStatus !== CourseOnlySessionStatus.editing
                ? 'Add to course'
                : 'Update session'
              : 'Save & continue'}
          </PrimaryLoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};

export default EventLocationsForm;
